import { Button, Input, Select, Space, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect } from "react";
import AddIcon from "../../../../../../assets/icons/AddIcon";
import EditIcon from "../../../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../../../assets/icons/ViewIcon";
import ConfirmModal from "../../../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../../../components/ui/CustomTable";
import {
  useGetNotificationTemplatesQuery,
  useUpdateNotificationTemplatesStatusMutation,
} from "../../../../../../features/notification/notificationApi";
import useError from "../../../../../../hooks/useError";
import useModal from "../../../../../../hooks/useModal";
import useTable from "../../../../../../hooks/useTable";
import { formatDate } from "../../../../../../lib/time";
import { toast } from "../../../../../../lib/toast";
import { GetNotificationTemplateParams } from "../../../../../../types/notification";
import { countParPageSerialNumber } from "../../../../../../utils/number";
import {
  addKeyInArray,
  selectSearchOption,
  skipData,
  underscoreToCapitalizeString,
} from "../../../../../../utils/utils";
import ManageMailTemplate from "./ManageMailTemplate";
import ViewMailTemplate from "./ViewMailTemplate";

type StatusType = {
  id: number;
  body: {
    is_active: boolean;
  };
};

export default function TemplateBuilderPanel() {
  const { open, handleOpen, handleClose } = useModal<
    StatusType | number,
    "STATUS"
  >();
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    handleSearch,
    keyword,
    filter,
    handleFilter,
  } = useTable<GetNotificationTemplateParams>();
  const { data, isLoading, isError, error } = useGetNotificationTemplatesQuery({
    params: {
      ...filter,
      limit,
      skip: skipData(page, limit),
      template_name: keyword,
    },
  });

  const [
    updateNotificationTemplatesStatus,
    {
      isSuccess: isUpdateSuccess,
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateNotificationTemplatesStatusMutation();
  useError(isError, error, "Failed fetching template");
  useError(
    isUpdateError,
    updateError,
    `Template ${
      typeof open.data !== "number" && open?.data?.body?.is_active
        ? "active"
        : "inactive"
    } fail`
  );

  const columns: ColumnsType<any> = [
    {
      title: "#SL",
      dataIndex: "index",
      align: "center",
      width: 50,
      render: (_: any, __: any, index: number) =>
        countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Template Name",
      dataIndex: "template_name",
      render: (val) => underscoreToCapitalizeString(val),
    },
    {
      title: "Mail Subject",
      dataIndex: "mail_subject",
    },
    {
      title: "Last Edited",
      dataIndex: "updated_at",
      render: (val) => (val ? formatDate(val) : "n/a"),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (val, record) => (
        <Space size={8}>
          <Switch
            className="custom-switch"
            checked={val}
            disabled={isUpdateLoading}
            onChange={(checked) => {
              handleOpen({
                type: "STATUS",
                data: {
                  id: record?.id,
                  body: {
                    is_active: checked ? true : false,
                  },
                },
              });
            }}
          />
          <span className="font-medium text-header">
            {val ? "Activated" : "Inactive"}
          </span>
        </Space>
      ),
    },
    {
      title: "Action",
      align: "center",
      dataIndex: "id",
      render: (val) => (
        <Space size={8}>
          <Button
            className="action-button"
            size="small"
            icon={<ViewIcon />}
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: val,
              })
            }
          />
          <Button
            icon={<EditIcon />}
            size="small"
            className="action-button"
            onClick={() =>
              handleOpen({
                type: "UPDATE",
                data: val,
              })
            }
          />
        </Space>
      ),
    },
  ];

  const handleStatus = () => {
    if (typeof open.data !== "number" && open?.data?.id) {
      updateNotificationTemplatesStatus({
        id: open.data?.id,
        body: open.data?.body,
      });
    }
  };

  useEffect(() => {
    if (isUpdateSuccess) {
      toast(
        "success",
        `Template successfully ${
          typeof open.data !== "number" && open?.data?.body?.is_active
            ? "activated"
            : "inactive"
        }`
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateSuccess]);

  return (
    <div className="mt-6 bg-white p-6 rounded-md border border-extra-light">
      <h3 className="text-header text-xl font-semibold mb-6">
        Template Builder Panel
      </h3>

      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total}
        dataSource={addKeyInArray(data?.data?.templates)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              onChange={handleSearch}
            />

            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              popupMatchSelectWidth={130}
              options={statusList}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              value={filter.is_active}
              onChange={(val) => handleFilter("is_active", val)}
            />

            <Button
              className="middle-button"
              type="primary"
              icon={<AddIcon />}
              onClick={() =>
                handleOpen({
                  type: "ADD",
                })
              }
            >
              Add New
            </Button>
          </>
        }
      />

      {(open.type === "ADD" || open.type === "UPDATE") &&
        (typeof open?.data === "number" ||
          typeof open?.data === "undefined") && (
          <ManageMailTemplate
            open={open.open}
            type={open.type}
            data={open.data}
            handleClose={handleClose}
          />
        )}
      {open.type === "VIEW" &&
        (typeof open?.data === "number" ||
          typeof open?.data === "undefined") && (
          <ViewMailTemplate
            open={open.open}
            handleClose={handleClose}
            data={open.data}
          />
        )}

      {open?.type === "STATUS" && (
        <ConfirmModal
          open={open.open}
          handleClose={handleClose}
          handleOk={handleStatus}
          confirmTitle="Wait!"
          questionText={`Are you sure you want to ${
            typeof open.data !== "number" && open?.data?.body?.is_active
              ? "active"
              : "inactive"
          } this template?`}
          okText="Yes"
          cancelText="No"
          okLoading={isUpdateLoading}
        />
      )}
    </div>
  );
}

const statusList = [
  {
    label: "Activated",
    value: true,
  },
  {
    label: "Inactive",
    value: false,
  },
];
